import { SegmentedMessage } from 'sms-segments-calculator'
import { TTextareaVariable } from '@honestica/curecall-ui'

const MAX_SMS_SEGMENTS = 8
const GSM_7_SEGMENT_LENGTH = 153
const UCS_2_SEGMENT_LENGTH = 67

/**
 * This function returns an array of characters that can't be encoded in GSM-7 from the `message` input
 * GSM-7 is the standard SMS encoding, allowing for (somewhat) long SMS segments but
 * with a limited character set.
 *
 * When a bigger character set is needed (using char ô for instance), another
 * encoding will be used automatically, allowing for a larger character set at the
 * cost of reduced segment length.
 *
 * In brief, when not using GSM-7 the SMS will be cut in shorter segments, needing
 * more segments for the whole SMS.
 * This will make the SMS costs more $ since an SMS price is defined by the number
 * of segments in it.
 *
 * This function helps identify culprit characters to help remove them from messages
 * @param {string} message
 * @returns {string[]}
 */
export function getNonGsmCharacters(message: string): string[] {
  const segmentedMessage = new SegmentedMessage(message)
  const isNonGsmCharDetected = segmentedMessage.getEncodingName() !== 'GSM-7'
  let nonGsmChars: string[] = []
  if (isNonGsmCharDetected) {
    nonGsmChars = segmentedMessage.getNonGsmCharacters()
  }
  return nonGsmChars
}

/**
 * Some operators refuse to deliver SMS containing more than 8 segments
 * @param {string} message
 * @returns {boolean}
 */
export function isMessageTooLong(message: string): boolean {
  const segmentedMessage = new SegmentedMessage(message)
  return segmentedMessage.segmentsCount > MAX_SMS_SEGMENTS
}

/**
 * Returns the maximum number of characters allowed for a message, based on the encoding it needs:
 * the limit is expressed in segments, and a segment holds fewer characters outside of GSM-7
 *
 * @param {string} message
 * @returns {number}
 */
export function getMaxMessageLength(message: string): number {
  const segmentedMessage = new SegmentedMessage(message)
  const segmentLength =
    segmentedMessage.getEncodingName() === 'GSM-7' ? GSM_7_SEGMENT_LENGTH : UCS_2_SEGMENT_LENGTH
  return MAX_SMS_SEGMENTS * segmentLength
}

/**
 * Calculates the number of chars of a given message based on the encoding used
 *
 * @param {string} message - The message for which to calculate the length.
 * @returns {number} The length of the message in number of characters.
 */
export function getMessageLength(message: string): number {
  const segmentedMessage = new SegmentedMessage(message)
  return segmentedMessage.numberOfCharacters
}

export const getTranslatedTextareaVariable = (variable: TTextareaVariable, t: any): TTextareaVariable => {
  return {
    label: t(`pages.configuration.messages.${variable.label}`),
    apiVariableSyntax: variable.apiVariableSyntax,
    isReadOnly: variable.isReadOnly,
    example: {
      average: variable.example.average,
      given: t(`pages.configuration.messages.${variable.example.given}`),
      result: t(`pages.configuration.messages.${variable.example.result}`),
    },
  }
}
